import {
  GoogleRecaptchaWidget,
  GoogleRecaptchaLibraryConfiguration,
  GoogleRecaptchaRenderOptions
} from "@indeed/bot-detection-multi-captcha-library";

// reCAPTCHA Enterprise via Indeed's paved-path bot-detection library
// (CDPE-704, BOTS-4497). The token is attached as the x-captcha-token header on
// createGuest / requestEmailChange and validated by the gateway.
//
// No-op when RECAPTCHA_SITE_KEY is unset: getCaptchaToken() resolves to null and
// no header is attached, so the frontend can ship before the Bots team
// provisions keys (the gateway verifier is likewise disabled until its keys are
// set). The reCAPTCHA badge is shown to satisfy Google's disclosure requirement
// without extra UI copy.

const SITE_KEY = process.env.RECAPTCHA_SITE_KEY || "";

let widget: GoogleRecaptchaWidget | null = null;

const getWidget = (): GoogleRecaptchaWidget | null => {
  if (!SITE_KEY || typeof window === "undefined") {
    return null;
  }
  if (!widget) {
    const libConfig: GoogleRecaptchaLibraryConfiguration = {
      application: "resume-com-landing",
      appPageName: "account",
      executeTimeoutMs: 10_000
    };
    const renderOptions: GoogleRecaptchaRenderOptions = {
      sitekey: SITE_KEY,
      badge: "bottomright"
    };
    widget = new GoogleRecaptchaWidget(libConfig, renderOptions);
  }

  return widget;
};

// Warm the widget as early as possible in the browser so a token is ready by the
// time the user triggers a protected action.
getWidget();

/**
 * Returns a reCAPTCHA token for the given action, or null when reCAPTCHA is not
 * configured / not ready / cannot be obtained (the client fails open; the
 * gateway still gates).
 */
export const getCaptchaToken = async (
  action: string
): Promise<string | null> => {
  const activeWidget = getWidget();
  if (!activeWidget) {
    return null;
  }

  try {
    if (!activeWidget.successfulInitialization) {
      return null;
    }
    const token = await activeWidget.getValidationToken(action);

    return token || null;
  } catch {
    return null;
  }
};

/**
 * Builds an Apollo `context` carrying the CAPTCHA token header for the given
 * action, or an empty object when no token is available.
 */
export const captchaContext = async (
  action: string
): Promise<{ headers?: { "x-captcha-token": string } }> => {
  const token = await getCaptchaToken(action);

  return token ? { headers: { "x-captcha-token": token } } : {};
};
